// src/pages/SkuUpdatePage.tsx
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import Swal from "sweetalert2";
import {
  Alert,
  Card,
  Checkbox,
  Divider,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import CustomButton from "src/components/common/CustomButton";
import { snackbarStore } from "src/store/snackbarStore";
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";
import type { SkuItem, SkuType } from "src/types/sku";
import {
  updateSkuApi,
  getSkuMappingListApi,
  deleteSkuMappingApi,
  updateSkuMappingApi,
  type SkuMappingItem,
} from "src/api/sku/skuApi";
import ProductVariantSelectModal from "src/components/sku/ProductVariantSelectModal";
import AppInputNumber from "src/components/common/AppInputNumber";
import AppInput from "src/components/common/AppInput";
import { getWarehouseListApi, type WarehouseListItem } from "src/api/warehouse/warehouseApi";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function SkuUpdatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const skuId = id ? Number(id) : null;

  const [skuCodeValue, setSkuCodeValue] = React.useState("");
  const [skuNameValue, setSkuNameValue] = React.useState("");
  const [skuDescriptionValue, setSkuDescriptionValue] = React.useState("");
  const [skuTypeValue, setSkuTypeValue] = React.useState<SkuType>("PRODUCT");
  const [barcodeValue, setBarcodeValue] = React.useState("");
  const [modelNumberValue, setModelNumberValue] = React.useState("");
  const [skuCategoryValue, setSkuCategoryValue] = React.useState("");
  const [manufacturerValue, setManufacturerValue] = React.useState("");
  const [originCountryValue, setOriginCountryValue] = React.useState("");
  const [weightValue, setWeightValue] = React.useState(0);
  const [lengthValue, setLengthValue] = React.useState(0);
  const [widthValue, setWidthValue] = React.useState(0);
  const [heightValue, setHeightValue] = React.useState(0);
  const [isActiveValue, setIsActiveValue] = React.useState(true);
  const [warehouseId, setWarehouseId] = React.useState<number | undefined>(undefined);

  const [warehouseList, setWarehouseList] = React.useState<WarehouseListItem[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [productModalOpen, setProductModalOpen] = React.useState(false);

  // 수정된 매핑 수량 추적 (mappingId -> quantityPerOrder)
  const [editedQuantities, setEditedQuantities] = React.useState<Record<number, number>>({});
  const [savingMappings, setSavingMappings] = React.useState(false);

  // SKU 상세 조회
  const { data: skuData, isLoading: skuLoading } = useSWR(
    skuId ? ["sku-detail", skuId] : null,
    async () => {
      const res = await axiosInstance.get<ApiResponse<SkuItem>>(`/v1/admin/baseinfo/sku/${skuId}`);
      return res.data;
    },
    { revalidateOnFocus: false }
  );

  const sku = (skuData as any)?.data;

  // SKU-Product 매핑 목록 조회
  const {
    data: mappingData,
    isLoading: mappingLoading,
    mutate: mutateMappings,
  } = useSWR(
    skuId ? ["sku-mapping", skuId] : null,
    async () => {
      return await getSkuMappingListApi({
        skuId: skuId!,
        page: 0,
        size: 100,
        order: "createdAt,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const mappings = (mappingData as any)?.data?.content ?? [];

  // 매핑 데이터가 변경되면 수정 내역 초기화
  React.useEffect(() => {
    setEditedQuantities({});
  }, [mappingData]);

  // Warehouse 목록 로드
  React.useEffect(() => {
    getWarehouseListApi({ page: 0, size: 1000, order: "warehouseName,asc" })
      .then((res) => {
        if (res.statusCode === 1 && res.data) {
          setWarehouseList(res.data.content);
        }
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!sku) return;
    setSkuCodeValue(sku.skuCode || "");
    setSkuNameValue(sku.skuName || "");
    setSkuDescriptionValue(sku.skuDescription || "");
    setSkuTypeValue(sku.skuType || "PRODUCT");
    setBarcodeValue(sku.barcode || "");
    setModelNumberValue(sku.modelNumber || "");
    setSkuCategoryValue(sku.skuCategory || "");
    setManufacturerValue(sku.manufacturer || "");
    setOriginCountryValue(sku.originCountry || "");
    setWeightValue(sku.weight || 0);
    setLengthValue(sku.length || 0);
    setWidthValue(sku.width || 0);
    setHeightValue(sku.height || 0);
    setIsActiveValue(sku.isActive ?? true);
    setWarehouseId((sku as any).warehouseId);
  }, [sku]);

  const handleSubmit = async () => {
    if (!skuId) {
      toast("SKU ID가 없습니다.", "error");
      return;
    }

    if (!skuNameValue.trim()) {
      toast("SKU명은 필수입니다.", "error");
      return;
    }

    setSubmitting(true);

    try {
      const res = await updateSkuApi(skuId, {
        id: skuId,
        skuCode: skuCodeValue.trim(),
        skuName: skuNameValue.trim(),
        skuDescription: skuDescriptionValue.trim(),
        manufacturer: manufacturerValue.trim(),
        originCountry: originCountryValue.trim(),
        weight: weightValue,
        length: lengthValue,
        width: widthValue,
        height: heightValue,
        barcode: barcodeValue.trim(),
        modelNumber: modelNumberValue.trim(),
        skuCategory: skuCategoryValue.trim(),
        warehouseId: warehouseId,
        isActive: isActiveValue,
      });

      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (code === 0 || code === 1) {
        toast("SKU가 수정되었습니다.", "success");
        navigate("/sku");
      } else {
        toast((res as any)?.message || "SKU 수정 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMapping = async (mappingId: number) => {
    const result = await Swal.fire({
      title: "삭제 확인",
      text: "이 연결을 삭제하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteSkuMappingApi(mappingId);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (code === 0 || code === 1) {
        toast("연결이 삭제되었습니다.", "success");
        mutateMappings();
      } else {
        toast((res as any)?.message || "삭제 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "삭제 중 오류가 발생했습니다.", "error");
    }
  };

  const handleQuantityChange = (
    mappingId: number,
    originalQuantity: number,
    newQuantity: number
  ) => {
    if (newQuantity === originalQuantity) {
      // 원래 값으로 되돌리면 수정 목록에서 제거
      setEditedQuantities((prev) => {
        const updated = { ...prev };
        delete updated[mappingId];
        return updated;
      });
    } else {
      // 수정된 값으로 추가
      setEditedQuantities((prev) => ({
        ...prev,
        [mappingId]: newQuantity,
      }));
    }
  };

  const handleSaveMappings = async () => {
    if (Object.keys(editedQuantities).length === 0) {
      toast("수정된 항목이 없습니다.", "info");
      return;
    }

    setSavingMappings(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const [mappingIdStr, quantityPerOrder] of Object.entries(editedQuantities)) {
        const mappingId = Number(mappingIdStr);
        try {
          const res = await updateSkuMappingApi(mappingId, { quantityPerOrder });
          const code = (res as any)?.statusCode ?? (res as any)?.status_code;
          if (code === 0 || code === 1) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (e) {
          failCount++;
        }
      }

      if (failCount === 0) {
        toast(`${successCount}개의 연결이 수정되었습니다.`, "success");
        setEditedQuantities({});
        mutateMappings();
      } else {
        toast(`${successCount}개 성공, ${failCount}개 실패`, "warning");
        mutateMappings();
      }
    } catch (e: any) {
      toast(e?.message || "저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSavingMappings(false);
    }
  };

  const mappingColumns: ColumnsType<SkuMappingItem> = [
    {
      title: "선택",
      width: 60,
      align: "center",
      render: () => <Checkbox disabled />,
    },
    {
      title: "상품번호 (스마트토어)",
      dataIndex: "productCode",
      key: "productCode",
      width: 180,
      align: "left",
    },
    {
      title: "상품명 (한도)",
      dataIndex: "productName",
      key: "productName",
      width: 220,
      align: "left",
    },
    {
      title: "옵션ID",
      dataIndex: "variantId",
      key: "variantId",
      width: 70,
      align: "center",
    },
    {
      title: "옵션명",
      dataIndex: "variantName",
      key: "variantName",
      width: 130,
      align: "left",
    },
    {
      title: "주문당 출고수량 ⓘ",
      dataIndex: "quantityPerOrder",
      key: "quantityPerOrder",
      width: 140,
      align: "right",
      render: (value: number, record) => {
        const currentValue = editedQuantities[record.mappingId] ?? value;
        return (
          <AppInputNumber
            value={currentValue}
            onChange={(newValue) => handleQuantityChange(record.mappingId, value, newValue || 0)}
            min={1}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "판매상태",
      width: 100,
      align: "center",
      render: () => "-",
    },
    {
      title: "풀필먼트 협력",
      width: 120,
      align: "center",
      render: () => "-",
    },
    {
      title: "삭제",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <CustomButton
          size="small"
          customVariant="danger"
          onClick={() => handleDeleteMapping(record.mappingId)}
        >
          삭제
        </CustomButton>
      ),
    },
  ];

  if (!skuId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" description="SKU ID가 유효하지 않습니다." />
      </div>
    );
  }

  if (skuLoading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!sku) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" description="SKU를 찾을 수 없습니다." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, width: "100%" }}>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            SKU 수정
          </Typography.Title>
          <Space>
            <CustomButton variant="outlined" onClick={() => navigate("/sku")}>
              취소
            </CustomButton>
            <CustomButton variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "저장 중..." : "저장"}
            </CustomButton>
          </Space>
        </div>

        <Divider />

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* 기본 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              기본 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">SKU 코드 *</Typography.Text>
                <AppInput value={skuCodeValue} disabled />
              </div>
              <div>
                <Typography.Text type="secondary">SKU명 *</Typography.Text>
                <AppInput
                  value={skuNameValue}
                  onChange={(e) => setSkuNameValue(e.target.value)}
                  placeholder="상품명"
                />
              </div>
              <div>
                <Typography.Text type="secondary">SKU 타입 *</Typography.Text>
                <Select<SkuType>
                  value={skuTypeValue}
                  disabled
                  options={[{ label: "PRODUCT", value: "PRODUCT" }]}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <Typography.Text type="secondary">창고</Typography.Text>
                <Select
                  value={warehouseId}
                  onChange={(value) => setWarehouseId(value)}
                  placeholder="창고 선택"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  options={warehouseList.map((w) => ({
                    label: w.warehouseName,
                    value: w.warehouseId,
                  }))}
                />
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">SKU 설명</Typography.Text>
              <Input.TextArea
                value={skuDescriptionValue}
                placeholder="설명"
                onChange={(e) => setSkuDescriptionValue(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <Divider />

          {/* 식별 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              식별 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">바코드</Typography.Text>
                <AppInput
                  value={barcodeValue}
                  onChange={(e) => setBarcodeValue(e.target.value)}
                  placeholder="바코드"
                />
              </div>
              <div>
                <Typography.Text type="secondary">모델 번호</Typography.Text>
                <AppInput
                  value={modelNumberValue}
                  onChange={(e) => setModelNumberValue(e.target.value)}
                  placeholder="모델번호"
                />
              </div>
              <div>
                <Typography.Text type="secondary">SKU 카테고리</Typography.Text>
                <AppInput
                  value={skuCategoryValue}
                  onChange={(e) => setSkuCategoryValue(e.target.value)}
                  placeholder="카테고리"
                />
              </div>
              <div>
                <Typography.Text type="secondary">제조사</Typography.Text>
                <AppInput
                  value={manufacturerValue}
                  onChange={(e) => setManufacturerValue(e.target.value)}
                  placeholder="제조사"
                />
              </div>
              <div>
                <Typography.Text type="secondary">원산지</Typography.Text>
                <AppInput
                  value={originCountryValue}
                  onChange={(e) => setOriginCountryValue(e.target.value)}
                  placeholder="원산지"
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* 치수 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              치수 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">무게</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={weightValue}
                  onChange={(val) => setWeightValue(val || 0)}
                  placeholder="g"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">길이</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={lengthValue}
                  onChange={(val) => setLengthValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">너비</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={widthValue}
                  onChange={(val) => setWidthValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">높이</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={heightValue}
                  onChange={(val) => setHeightValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* 기타 설정 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              기타 설정
            </Typography.Text>
            <Space size={8} align="center">
              <Switch checked={isActiveValue} onChange={(checked) => setIsActiveValue(checked)} />
              <Typography.Text>활성 여부</Typography.Text>
            </Space>
          </div>

          <Divider />

          {/* 연결상품 */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Typography.Text strong style={{ fontSize: 15 }}>
                연결상품
              </Typography.Text>
              <Space>
                <CustomButton onClick={() => setProductModalOpen(true)}>상품 불러오기</CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSaveMappings}
                  disabled={Object.keys(editedQuantities).length === 0 || savingMappings}
                >
                  {savingMappings ? "저장 중..." : "저장"}
                </CustomButton>
              </Space>
            </div>
            <Alert
              type="info"
              description="연결된 상품의 주문이 발생하면, 출고시에 해당 SKU가 출고됩니다. 연결상품 수정은 오로지 방치된 것을 위해 상품의 관리페이지가 '판매중지' 상태에서만 가능합니다."
              style={{ marginBottom: 12 }}
            />
            <Table
              columns={mappingColumns}
              dataSource={mappings}
              loading={mappingLoading}
              rowKey="mappingId"
              pagination={false}
              scroll={{ x: 1000 }}
              locale={{
                emptyText:
                  "새로운 연결 상품을 추가해 주세요.\n추가된 연결 상품은 기존 목록에 반영됩니다.",
              }}
            />
          </div>
        </div>
      </Card>

      {/* 상품 선택 모달 */}
      <ProductVariantSelectModal
        open={productModalOpen}
        skuId={skuId}
        onClose={() => setProductModalOpen(false)}
        onSuccess={() => mutateMappings()}
      />
    </div>
  );
}
